import type { Campaign, ReportArea, Task, WorkCategory } from '../types'
import { taskMatchesOperation } from './operationScope'
import { tasksForContributor } from './productivityData'

export type PeriodPreset = 'day' | 'week' | 'month' | 'quarter' | 'semester' | 'year' | 'all'
export const reportingStartDate = '2026-08-01'

export const periodOptions: Array<{ value: PeriodPreset; label: string }> = [
  { value: 'day', label: 'Dia' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mês' },
  { value: 'quarter', label: 'Trimestre' },
  { value: 'semester', label: 'Semestre' },
  { value: 'year', label: 'Ano' },
  { value: 'all', label: 'Todo período' },
]

export const reportAreas: ReportArea[] = ['Mídia e Promoção', 'Digital', 'Branding Institucional', 'Gestão de Demandas', 'Farmácia', 'Marcas Próprias', 'Trade', 'Patrocínios', 'Comunicação Visual', 'Comunicação Regional']

export interface DashboardAreaMetric {
  area: ReportArea
  total: number
  percentage: number
  alterations?: number
  alterationRate?: number
  change?: number
  note?: string
}

export interface DashboardSnapshot {
  label: string
  source: 'reference' | 'live' | 'mixed'
  start: string
  end: string
  total: number
  totalChange?: number
  alterations: number
  alterationRate: number
  alterationConcentration?: number
  digitalTotal: number
  digitalInternal: number
  digitalOther: number
  digitalChange?: number
  yearTotal: number
  previousYearTotal: number
  yearChange?: number
  monthly: Array<{ label: string; total: number; selected?: boolean }>
  areas: DashboardAreaMetric[]
  areaTotal: number
  insight: string
  focus: string[]
}

const areaByCategory: Record<WorkCategory, ReportArea> = {
  Diagramação: 'Mídia e Promoção',
  Revisão: 'Branding Institucional',
  'Criação/Redação': 'Digital',
  'Gestão/Planejamento': 'Gestão de Demandas',
}

const referenceMonthly = [
  { key: '2025-08', label: 'ago/25', total: 2006 }, { key: '2025-09', label: 'set/25', total: 2458 },
  { key: '2025-10', label: 'out/25', total: 2429 }, { key: '2025-11', label: 'nov/25', total: 2997 },
  { key: '2025-12', label: 'dez/25', total: 2941 }, { key: '2026-01', label: 'jan/26', total: 2142 },
  { key: '2026-02', label: 'fev/26', total: 1211 }, { key: '2026-03', label: 'mar/26', total: 2134 },
  { key: '2026-04', label: 'abr/26', total: 2507 }, { key: '2026-05', label: 'mai/26', total: 1831 },
  { key: '2026-06', label: 'jun/26', total: 1743 }, { key: '2026-07', label: 'jul/26', total: 1706 },
  { key: '2026-08', label: 'ago/26', total: 2231 },
] as const

const referenceAreas: DashboardAreaMetric[] = [
  { area: 'Mídia e Promoção', total: 886, percentage: 40, change: 10, note: 'Impulsionada pela Campanha de Aniversário 52 anos.' },
  { area: 'Digital', total: 362, percentage: 16, change: -12, note: '620 entregas digitais consolidadas com outras áreas.' },
  { area: 'Branding Institucional', total: 319, percentage: 14, alterations: 81, alterationRate: 25 },
  { area: 'Gestão de Demandas', total: 227, percentage: 10, note: 'Volume de entrega da Gestão House.' },
  { area: 'Farmácia', total: 176, percentage: 8, alterations: 56, alterationRate: 32 },
  { area: 'Marcas Próprias', total: 84, percentage: 4, alterations: 50, alterationRate: 60 },
  { area: 'Trade', total: 73, percentage: 3, change: 462, note: 'Destaque para Festival das Delícias.' },
  { area: 'Patrocínios', total: 67, percentage: 3, change: 52, note: 'Segundo maior volume de 2026.' },
  { area: 'Comunicação Visual', total: 37, percentage: 2, alterations: 0, alterationRate: 0, note: 'Volume estável com foco em loja.' },
]

function iso(date: Date) {
  return date.toISOString().slice(0, 10)
}

function utcDate(value: string) {
  return new Date(`${value}T12:00:00.000Z`)
}

function endOfMonth(year: number, month: number) {
  return new Date(Date.UTC(year, month + 1, 0, 12))
}

export function periodRange(preset: PeriodPreset, anchor: string) {
  const date = utcDate(anchor)
  const year = date.getUTCFullYear()
  const month = date.getUTCMonth()
  let start = new Date(date)
  let end = new Date(date)

  if (preset === 'week') {
    const mondayOffset = (date.getUTCDay() + 6) % 7
    start.setUTCDate(date.getUTCDate() - mondayOffset)
    end = new Date(start)
    end.setUTCDate(start.getUTCDate() + 6)
    if (start < utcDate(reportingStartDate) && end >= utcDate(reportingStartDate)) start = utcDate(reportingStartDate)
  } else if (preset === 'month') {
    start = new Date(Date.UTC(year, month, 1, 12))
    end = endOfMonth(year, month)
  } else if (preset === 'quarter') {
    const quarterMonth = Math.floor(month / 3) * 3
    start = new Date(Date.UTC(year, quarterMonth, 1, 12))
    end = endOfMonth(year, quarterMonth + 2)
  } else if (preset === 'semester') {
    const semesterMonth = month < 6 ? 0 : 6
    start = new Date(Date.UTC(year, semesterMonth, 1, 12))
    end = endOfMonth(year, semesterMonth + 5)
  } else if (preset === 'year') {
    start = new Date(Date.UTC(year, 0, 1, 12))
    end = new Date(Date.UTC(year, 11, 31, 12))
  } else if (preset === 'all') {
    start = new Date(Date.UTC(2000, 0, 1, 12))
    end = new Date(Date.UTC(2100, 11, 31, 12))
  }

  return { start: iso(start), end: iso(end) }
}

function periodLabel(preset: PeriodPreset, start: string, end: string) {
  if (preset === 'all') return 'Todo o período'
  const startDate = utcDate(start)
  const year = startDate.getUTCFullYear()
  if (preset === 'quarter') return `${Math.floor(startDate.getUTCMonth() / 3) + 1}º trimestre de ${year}`
  if (preset === 'semester') return `${startDate.getUTCMonth() < 6 ? 1 : 2}º semestre de ${year}`
  if (preset === 'year') return String(year)
  if (preset === 'month') return new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(startDate)
  const formatter = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })
  if (preset === 'day') return formatter.format(startDate).replace('.', '')
  return `${formatter.format(startDate).replace('.', '')} – ${formatter.format(utcDate(end)).replace('.', '')}`
}

function belongsToReferenceBase(task: Task, campaigns: Campaign[]) {
  return campaigns.find(item => item.id === task.campaignId)?.clientId === 'assai'
}

function taskArea(task: Task, campaigns: Campaign[]) {
  return task.reportArea ?? campaigns.find(campaign => campaign.id === task.campaignId)?.reportArea ?? areaByCategory[task.category]
}

function deliveryArea(task: Task, subtask: Task['subtasks'][number], campaigns: Campaign[]) {
  return subtask.reportArea ?? taskArea(task, campaigns)
}

function statusChangeArea(task: Task, entry: Task['activityEntries'][number], campaigns: Campaign[]) {
  if (entry.statusChange?.entity === 'deliverable') {
    return task.subtasks.find(subtask => subtask.id === entry.statusChange?.entityId)?.reportArea ?? taskArea(task, campaigns)
  }
  return taskArea(task, campaigns)
}

function inRange(value: string | undefined, start: string, end: string) {
  if (!value) return false
  const day = value.slice(0, 10)
  return day >= start && day <= end
}

function percentChange(current: number, previous: number) {
  return previous ? Math.round((current - previous) / previous * 100) : undefined
}

function previousRange(start: string, end: string) {
  const startDate = utcDate(start)
  const endDate = utcDate(end)
  const duration = endDate.getTime() - startDate.getTime() + 86_400_000
  const previousEnd = new Date(startDate.getTime() - 86_400_000)
  const previousStart = new Date(previousEnd.getTime() - duration + 86_400_000)
  return { start: iso(previousStart), end: iso(previousEnd) }
}

function monthKey(date: Date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
}

function monthLabel(key: string) {
  return new Intl.DateTimeFormat('pt-BR', { month: 'short', year: '2-digit', timeZone: 'UTC' }).format(utcDate(`${key}-01`)).replace('.', '')
}

function referenceSnapshot(): DashboardSnapshot {
  return {
    label: 'agosto de 2026', source: 'reference', start: '2026-08-01', end: '2026-08-31',
    total: 2231, totalChange: 11, alterations: 373, alterationRate: 17, alterationConcentration: 50,
    digitalTotal: 620, digitalInternal: 362, digitalOther: 258, digitalChange: 51,
    yearTotal: 15687, previousYearTotal: 14974, yearChange: 5,
    monthly: referenceMonthly.map((item, index) => ({ label: item.label, total: item.total, selected: index === referenceMonthly.length - 1 })),
    areas: referenceAreas, areaTotal: 2231,
    insight: 'O período alcançou 2.231 entregas, com forte contribuição de Mídia e Promoção e Digital. Metade das alterações ficou concentrada em Branding, Farmácia e Marcas Próprias.',
    focus: [],
  }
}

function referencePeriodSnapshot(tasks: Task[], campaigns: Campaign[], operation: string, preset: PeriodPreset, start: string, end: string): DashboardSnapshot {
  const scopedTasks = tasks.filter(task => taskMatchesOperation(task, campaigns, operation))
  const isAll = preset === 'all'
  const isAdditionalLiveRecord = (task: Task, date?: string) => Boolean(
    date && (date.slice(0, 7) !== '2026-08' || (operation === 'Todas as operações' && !belongsToReferenceBase(task, campaigns))),
  )
  const allLiveDeliveries = scopedTasks.flatMap(task => task.subtasks
    .filter(subtask => isAdditionalLiveRecord(task, subtask.completedAt))
    .map(subtask => ({ task, subtask })))
  const liveDeliveries = allLiveDeliveries.filter(({ subtask }) => isAll || inRange(subtask.completedAt, start, end))
  const allLiveStatusChanges = scopedTasks.flatMap(task => task.activityEntries
    .filter(entry => entry.statusChange?.to === 'Em alteração' && isAdditionalLiveRecord(task, entry.occurredOn))
    .map(entry => ({ task, entry })))
  const liveStatusChanges = allLiveStatusChanges.filter(({ entry }) => isAll || inRange(entry.occurredOn, start, end))
  const detailedHistoricalTotal = 2231
  const total = detailedHistoricalTotal + liveDeliveries.length

  const areaTotals = Object.fromEntries(reportAreas.map(area => [area, referenceAreas.find(item => item.area === area)?.total ?? 0])) as Record<ReportArea, number>
  const liveAreaAlterations = Object.fromEntries(reportAreas.map(area => [area, 0])) as Record<ReportArea, number>
  liveDeliveries.forEach(({ task, subtask }) => { areaTotals[deliveryArea(task, subtask, campaigns)] += 1 })
  liveStatusChanges.forEach(({ task, entry }) => { liveAreaAlterations[statusChangeArea(task, entry, campaigns)] += 1 })
  const areaTotal = Object.values(areaTotals).reduce((sum, value) => sum + value, 0)
  const areas: DashboardAreaMetric[] = reportAreas.map(area => {
    const referenceAlterations = referenceAreas.find(item => item.area === area)?.alterations
    const alterations = referenceAlterations !== undefined ? referenceAlterations + liveAreaAlterations[area] : liveAreaAlterations[area] || undefined
    return {
      area,
      total: areaTotals[area],
      percentage: areaTotal ? Math.round(areaTotals[area] / areaTotal * 100) : 0,
      alterations,
      alterationRate: alterations !== undefined && areaTotals[area] ? Math.round(alterations / areaTotals[area] * 100) : undefined,
    }
  }).sort((a, b) => b.total - a.total)

  const liveMonthly = new Map<string, number>()
  allLiveDeliveries.forEach(({ subtask }) => {
    const key = subtask.completedAt!.slice(0, 7)
    liveMonthly.set(key, (liveMonthly.get(key) ?? 0) + 1)
  })
  const monthlyKeys = [...new Set([...referenceMonthly.map(item => item.key), ...liveMonthly.keys()])].sort()
  const monthly = monthlyKeys.map(key => {
    const reference = referenceMonthly.find(item => item.key === key)
    return { label: reference?.label ?? monthLabel(key), total: reference?.total ?? liveMonthly.get(key) ?? 0, selected: !isAll && key >= start.slice(0, 7) && key <= end.slice(0, 7) }
  })
  const digitalInternal = 362 + liveDeliveries.filter(({ task, subtask }) => deliveryArea(task, subtask, campaigns) === 'Digital').length
  const digitalOther = 258 + liveDeliveries.filter(({ task, subtask }) => deliveryArea(task, subtask, campaigns) !== 'Digital' && (subtask.materialCategory === 'Mídia ON' || subtask.materialCategory === 'DOOH')).length
  const yearLive = allLiveDeliveries.filter(({ subtask }) => inRange(subtask.completedAt, '2026-01-01', end)).length
  const alterations = 373 + liveStatusChanges.length
  const liveRecordText = liveDeliveries.length === 1 ? '1 registro do StarTracker' : `${numberForInsight(liveDeliveries.length)} registros do StarTracker`
  const criticalAreas: ReportArea[] = ['Branding Institucional', 'Farmácia', 'Marcas Próprias']
  const liveCriticalAlterations = liveStatusChanges.filter(({ task, entry }) => criticalAreas.includes(statusChangeArea(task, entry, campaigns))).length

  const lastMonth = monthlyKeys.at(-1) ?? '2026-08'
  const [lastYear, lastMonthNumber] = lastMonth.split('-').map(Number)
  return {
    label: periodLabel(preset, start, end), source: liveDeliveries.length || liveStatusChanges.length ? 'mixed' : 'reference',
    start: isAll ? '2025-08-01' : start, end: isAll ? iso(endOfMonth(lastYear, lastMonthNumber - 1)) : end,
    total, alterations, alterationRate: total ? Math.round(alterations / total * 100) : 0,
    alterationConcentration: alterations ? Math.round((187 + liveCriticalAlterations) / alterations * 100) : 0,
    digitalTotal: digitalInternal + digitalOther, digitalInternal, digitalOther,
    yearTotal: 15687 + yearLive, previousYearTotal: 14974,
    yearChange: percentChange(15687 + yearLive, 14974), monthly, areas, areaTotal,
    insight: `${numberForInsight(total)} entregas possuem registro consolidado neste recorte: ${numberForInsight(detailedHistoricalTotal)} da base validada de agosto de 2026 e ${liveRecordText}. Os demais meses permanecem apenas como referência histórica de evolução.`,
    focus: [],
  }
}

function numberForInsight(value: number) {
  return value.toLocaleString('pt-BR')
}

export function buildDashboardSnapshot(tasks: Task[], campaigns: Campaign[], preset: PeriodPreset, anchor: string, operation: string, contributor?: string): DashboardSnapshot {
  const { start, end } = periodRange(preset, anchor)
  const dashboardTasks = contributor ? tasksForContributor(tasks, contributor) : tasks
  if (!contributor && preset === 'month' && start === '2026-08-01' && operation === 'Hub Sudeste') return referenceSnapshot()
  const usesReferenceBase = !contributor && (operation === 'Hub Sudeste' || operation === 'Todas as operações') &&
    (preset === 'all' || ((preset === 'month' || preset === 'quarter' || preset === 'semester' || preset === 'year') && start <= '2026-08-31' && end >= '2026-08-01'))
  if (usesReferenceBase) return referencePeriodSnapshot(dashboardTasks, campaigns, operation, preset, start, end)

  const scopedTasks = dashboardTasks.filter(task => taskMatchesOperation(task, campaigns, operation))
  const deliveries = scopedTasks.flatMap(task => task.subtasks.filter(subtask => inRange(subtask.completedAt, start, end)).map(subtask => ({ task, subtask })))
  const previous = previousRange(start, end)
  const previousDeliveries = scopedTasks.flatMap(task => task.subtasks.filter(subtask => inRange(subtask.completedAt, previous.start, previous.end)))
  const statusChanges = scopedTasks.flatMap(task => task.activityEntries.filter(entry => entry.statusChange?.to === 'Em alteração' && inRange(entry.occurredOn, start, end)).map(entry => ({ task, entry })))
  const total = deliveries.length
  const areaTotals = Object.fromEntries(reportAreas.map(area => [area, 0])) as Record<ReportArea, number>
  const areaAlterations = Object.fromEntries(reportAreas.map(area => [area, 0])) as Record<ReportArea, number>
  deliveries.forEach(({ task, subtask }) => { areaTotals[deliveryArea(task, subtask, campaigns)] += 1 })
  statusChanges.forEach(({ task, entry }) => { areaAlterations[statusChangeArea(task, entry, campaigns)] += 1 })
  const areas = reportAreas.map(area => ({
    area,
    total: areaTotals[area],
    percentage: total ? Math.round(areaTotals[area] / total * 100) : 0,
    alterations: areaAlterations[area],
    alterationRate: areaTotals[area] ? Math.round(areaAlterations[area] / areaTotals[area] * 100) : 0,
  })).sort((a, b) => b.total - a.total)
  const digitalInternal = areaTotals.Digital
  const digitalOther = deliveries.filter(({ task, subtask }) => deliveryArea(task, subtask, campaigns) !== 'Digital' && (subtask.materialCategory === 'Mídia ON' || subtask.materialCategory === 'DOOH')).length
  const yearStart = `${utcDate(anchor).getUTCFullYear()}-01-01`
  const yearDeliveries = scopedTasks.flatMap(task => task.subtasks.filter(subtask => inRange(subtask.completedAt, yearStart, end)))
  const previousYearStart = `${utcDate(anchor).getUTCFullYear() - 1}-01-01`
  const previousYearEnd = `${utcDate(anchor).getUTCFullYear() - 1}-${end.slice(5)}`
  const previousYearDeliveries = scopedTasks.flatMap(task => task.subtasks.filter(subtask => inRange(subtask.completedAt, previousYearStart, previousYearEnd)))
  const anchorDate = utcDate(anchor)
  const monthlyKeys = Array.from({ length: 13 }, (_, index) => {
    const date = new Date(Date.UTC(anchorDate.getUTCFullYear(), anchorDate.getUTCMonth() - 12 + index, 1, 12))
    return monthKey(date)
  })
  const monthly = monthlyKeys.map(key => ({ label: monthLabel(key), total: scopedTasks.flatMap(task => task.subtasks).filter(subtask => subtask.completedAt?.startsWith(key)).length, selected: key === anchor.slice(0, 7) }))
  const topArea = areas[0]
  return {
    label: periodLabel(preset, start, end), source: 'live', start, end, total,
    totalChange: percentChange(total, previousDeliveries.length), alterations: statusChanges.length,
    alterationRate: total ? Math.round(statusChanges.length / total * 100) : 0,
    digitalTotal: digitalInternal + digitalOther, digitalInternal, digitalOther,
    yearTotal: yearDeliveries.length, previousYearTotal: previousYearDeliveries.length,
    yearChange: percentChange(yearDeliveries.length, previousYearDeliveries.length), monthly, areas, areaTotal: total,
    insight: total ? `${total} entregas foram concluídas no período. ${topArea.area} liderou o volume com ${topArea.total} entrega(s).` : 'Ainda não há entregas concluídas para os filtros selecionados.',
    focus: [],
  }
}
